import React, { useMemo, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API_HOST =
  window.location.hostname === "test.workbrook.com"
    ? "https://test.workbrook.us"
    : "https://workbrook.us";

const statusConfig = {
  accepted: {
    icon: "✓",
    iconBg: "#4CAF50",
    title: "Salary Confirmed",
    message:
      "Thank you for confirming your salary. Your response has been recorded successfully.",
  },
  declined: {
    icon: "✕",
    iconBg: "#f44336",
    title: "Salary Declined",
    message:
      "Your response has been recorded. The hiring team has been notified and will review your application.",
  },
  already_responded: {
    icon: "ℹ",
    iconBg: "#FF9800",
    title: "Already Responded",
    message:
      "You have already responded to this salary confirmation. No further action is needed.",
  },
};

const ConfirmSalary = () => {
  const { search } = useLocation();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const { status, original, data } = useMemo(() => {
    const params = new URLSearchParams(search);
    return {
      status: params.get("status"),
      original: params.get("original"),
      data: params.get("data"),
    };
  }, [search]);

  useEffect(() => {
    if (data && !status) {
      setLoading(true);
      axios
        .get(`${API_HOST}/confirm-salary`, {
          params: { data, format: "json" },
        })
        .then((res) => {
          setResult(res.data);
        })
        .catch((err) => {
          const msg =
            err.response?.data?.message ||
            "An error occurred while processing your request.";
          setError(msg);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [data, status]);

  const resolvedStatus = status || result?.status;
  const resolvedOriginal = original || result?.original;

  const config = loading
    ? null
    : error
    ? {
        icon: "!",
        iconBg: "#f44336",
        title: "Error",
        message: error,
      }
    : statusConfig[resolvedStatus] || {
        icon: "?",
        iconBg: "#9E9E9E",
        title: "Unknown Status",
        message:
          "We could not determine the status of your salary confirmation.",
      };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        fontFamily: "'Poppins', sans-serif",
        padding: "20px",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "20px",
          padding: "50px 40px",
          maxWidth: "480px",
          width: "100%",
          textAlign: "center",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        }}
      >
        <img
          alt="Workbrook"
          src="https://workbrook.com/wp-content/uploads/2020/06/WBtext.png"
          style={{ height: "28px", marginBottom: "30px" }}
        />

        {loading ? (
          <p style={{ fontSize: "16px", color: "#555" }}>
            Processing your response...
          </p>
        ) : (
          <>
            <div
              style={{
                width: "70px",
                height: "70px",
                borderRadius: "50%",
                background: config.iconBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 24px",
                fontSize: "32px",
                color: "#fff",
                fontWeight: "bold",
              }}
            >
              {config.icon}
            </div>

            <h1
              style={{
                fontSize: "22px",
                fontWeight: 600,
                color: "#1f1f1f",
                margin: "0 0 12px",
              }}
            >
              {config.title}
            </h1>

            <p
              style={{
                fontSize: "15px",
                color: "#555",
                lineHeight: 1.6,
                margin: "0 0 8px",
              }}
            >
              {config.message}
            </p>

            {resolvedStatus === "already_responded" && resolvedOriginal && (
              <p
                style={{
                  fontSize: "13px",
                  color: "#888",
                  margin: "8px 0 0",
                }}
              >
                Your previous response:{" "}
                <strong style={{ textTransform: "capitalize" }}>
                  {resolvedOriginal}
                </strong>
              </p>
            )}
          </>
        )}

        <hr
          style={{
            border: "none",
            borderTop: "1px solid #eee",
            margin: "30px 0",
          }}
        />

        <p style={{ fontSize: "13px", color: "#999", margin: 0 }}>
          You may close this page. If you have any questions, please contact{" "}
          <a
            href="mailto:support@workbrook.com"
            style={{ color: "#3A6ED4", textDecoration: "none" }}
          >
            support@workbrook.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default ConfirmSalary;
